import type Konva from 'konva';
import { useCallback, useEffect, useRef } from 'react';
import { useCanvasStore } from '../store/canvasStore';

const useTextEditing = () => {
  const isEditingTextId = useCanvasStore((s) => s.isEditingTextId);
  const setIsEditingTextId = useCanvasStore((s) => s.setIsEditingTextId);
  const setEditingStyle = useCanvasStore((s) => s.setEditingStyle);
  const textRefs = useCanvasStore((s) => s.textRefs);
  const stageRef = useCanvasStore((s) => s.stageRef);
  const setSelectedId = useCanvasStore((s) => s.setSelectedId);
  const texts = useCanvasStore((s) => s.texts);

  const currentEditingId = useRef<string | null>(isEditingTextId);

  useEffect(() => {
    currentEditingId.current = isEditingTextId;
  }, [isEditingTextId]);

  const updateEditorPosition = useCallback(() => {
    if (!isEditingTextId || !stageRef.current) return;

    const textNode = stageRef.current.findOne(`#text-${isEditingTextId}`) as Konva.Node;
    if (!textNode) return;

    const rect = textNode.getClientRect();
    const stageBox = stageRef.current.container().getBoundingClientRect();
    const borderAdjustment = 2;

    setEditingStyle({
      top: rect.y + stageBox.top - borderAdjustment,
      left: rect.x + stageBox.left - borderAdjustment,
      width: rect.width + borderAdjustment * 2,
      height: rect.height + borderAdjustment * 2,
    });
  }, [isEditingTextId, stageRef, setEditingStyle]);

  const handleTextDoubleClick = useCallback(
    (id: string): void => {
      const textItem = texts.find((t) => t.id === id);
      if (!textItem) return;

      setIsEditingTextId(id);
      setSelectedId(id);

      setTimeout(() => {
        const textNode = textRefs.current?.get(id);
        if (!textNode || !stageRef.current) return;

        const rect = (textNode as unknown as Konva.Node).getClientRect();
        const stageBox = stageRef.current.container().getBoundingClientRect();
        const borderAdjustment = 2;

        setEditingStyle({
          top: rect.y + stageBox.top - borderAdjustment,
          left: rect.x + stageBox.left - borderAdjustment,
          width: rect.width + borderAdjustment * 2,
          height: rect.height + borderAdjustment * 2,
        });
      }, 10);
    },
    [setIsEditingTextId, textRefs, stageRef, setEditingStyle, texts, setSelectedId]
  );

  useEffect(() => {
    return () => {
      const id = currentEditingId.current;
      if (id) {
        setIsEditingTextId(null);
        setSelectedId(id);
      }
    };
  }, [setIsEditingTextId, setSelectedId]);

  return {
    updateEditorPosition,
    handleTextDoubleClick,
  };
};

export default useTextEditing;
